import React, { useState } from 'react'
import { ChevronDown, ChevronRight, Lock } from 'lucide-react'

/**
 * CaseGroup — v242.73.0 (CASEHOME-GROUPS-1)
 *
 * The bottom half of Case Home used to be three unrelated presentations stacked
 * on top of each other: appointment CARDS, a separate doctor CARD, a 2-up
 * RESOURCES grid of tiles, and then two loose outline buttons for archive and
 * delete under a hairline. Four visual languages for "things about this case
 * that are not the 4D flow", all permanently expanded, so the patient scrolled
 * past everything to reach anything.
 *
 * Three collapsible groups instead. The point of the header is the SUMMARY: a
 * group must say what is inside it while it is shut ("Tomorrow, 9:00 AM",
 * "1 needs you", "2 encounters"), otherwise collapsing has only hidden
 * information rather than compressed it.
 */

const LINE_BORDER = '1px solid #F1F5F8'

export function Tag({ children, tone = 'default' }) {
  const tones = {
    default: { background: '#F1F5F8', color: '#64748B' },
    ok: { background: '#E7F8EC', color: '#1B873A' },
    info: { background: '#E9F8FF', color: '#1A6FC2' },
    warn: { background: '#FEF3C7', color: '#92400E' },
  }
  return (
    <span style={{
      marginLeft: 'auto', fontWeight: 700, fontSize: 10.5, lineHeight: 1,
      borderRadius: 999, padding: '4px 9px', whiteSpace: 'nowrap',
      ...(tones[tone] || tones.default),
    }}>
      {children}
    </span>
  )
}

/**
 * One row inside a group.
 *
 * `locked` greys the row and removes the tap rather than hiding it — the
 * patient should be able to see that a medical report exists and learn WHEN it
 * arrives, which is what the trailing tag is for.
 */
export function GroupLine({ icon: Icon, iconBg, iconColor, title, sub, tag, onClick, locked = false, first = false, photo = null, highlight = false }) {
  const tappable = !!onClick && !locked
  // v242.101.0 (TF b38) — "MD photo should appear here". The doctor row rendered
  // a generic UserCheck in the tile while `case.doctor.profile_photo` was sitting
  // in the payload unread (the API has hydrated it for patient reads since
  // v222.4.8; DiagnoseScreen's doctor card has always shown it).
  // ★ A remote image can 404 — the stale-storage-URL class is documented in the
  //   API at index.py:438 — so a failed load falls back to the icon rather than
  //   leaving a blank tile where a face should be.
  const [photoFailed, setPhotoFailed] = useState(false)
  const showPhoto = !!photo && !photoFailed && !locked
  return (
    <div
      onClick={tappable ? onClick : undefined}
      role={tappable ? 'button' : undefined}
      tabIndex={tappable ? 0 : undefined}
      onKeyDown={tappable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick() } : undefined}
      className={tappable ? 'active:opacity-70 transition-opacity cursor-pointer' : ''}
      data-group-line-highlight={highlight ? '' : undefined}
      style={highlight ? {
        // ★ s105 (TestFlight b71, Dr. Rami: "Hard to see this - blends into background. Make more
        //   noticeable and that its clickable"): the share-code line is a tinted, bordered card with a
        //   bold code and a visible chevron, set apart from the plain lines around it.
        display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px', margin: first ? '2px 0 6px' : '8px 0 6px',
        background: '#F0F8FF', border: '1.5px solid #A7D9FC', borderRadius: 14, fontSize: 13,
      } : {
        display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0',
        borderTop: first ? 'none' : LINE_BORDER, fontSize: 13,
      }}
    >
      {(Icon || showPhoto) && (
        <span style={{
          width: 29, height: 29, borderRadius: 9, flex: '0 0 29px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
          background: locked ? '#F1F5F8' : (iconBg || '#E9F8FF'),
          color: locked ? '#9AA8B6' : (iconColor || '#1A6FC2'),
        }}>
          {showPhoto ? (
            <img
              src={photo}
              alt=""
              onError={() => setPhotoFailed(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            Icon && <Icon size={14} strokeWidth={2.2} />
          )}
        </span>
      )}
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', color: locked ? '#9AA8B6' : '#15354F', ...(highlight ? { fontSize: 15, fontWeight: 800, letterSpacing: 0.3 } : {}) }}>{title}</span>
        {sub && <span style={{ display: 'block', fontSize: 11.5, color: '#64748B' }}>{sub}</span>}
      </span>
      {highlight && tappable && tag !== undefined && tag !== null && (
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {tag}
          <ChevronRight size={18} strokeWidth={2.4} style={{ color: '#46A8F9' }} />
        </span>
      )}
      {highlight && tappable && tag !== undefined && tag !== null ? null : tag !== undefined && tag !== null
        ? tag
        : (tappable && highlight)
          ? <ChevronRight size={18} strokeWidth={2.4} style={{ marginLeft: 'auto', color: '#46A8F9', flexShrink: 0 }} />
          : (tappable
            ? <ChevronRight size={14} style={{ marginLeft: 'auto', color: '#B6C2CE', flexShrink: 0 }} />
            : (locked ? <Lock size={13} style={{ marginLeft: 'auto', color: '#C3CEDA', flexShrink: 0 }} /> : null))}
    </div>
  )
}

export default function CaseGroup({ icon: Icon, name, meta, defaultOpen = false, children, id }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div id={id} style={{
      background: '#fff', border: '1px solid #E8EDF2', borderRadius: 16,
      overflow: 'hidden', marginBottom: 10,
    }}>
      <div
        onClick={() => setOpen(o => !o)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen(o => !o) }}
        aria-expanded={open}
        className="cursor-pointer active:opacity-80 transition-opacity"
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px' }}
      >
        {Icon && (
          <span style={{
            width: 29, height: 29, borderRadius: 9, flex: '0 0 29px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#E9F8FF', color: '#1A6FC2',
          }}>
            <Icon size={14} strokeWidth={2.2} />
          </span>
        )}
        <span style={{ fontWeight: 800, fontSize: 13, lineHeight: 1, color: '#15354F' }}>{name}</span>
        {/* The whole reason a group can be shut. */}
        {meta ? (
          <span style={{ fontSize: 11.5, color: '#64748B', marginLeft: 'auto', marginRight: 9 }}>{meta}</span>
        ) : <span style={{ marginLeft: 'auto' }} />}
        <ChevronDown
          size={14}
          style={{
            color: '#B6C2CE', flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform .2s',
          }}
        />
      </div>
      {open && <div style={{ padding: '0 14px 12px' }}>{children}</div>}
    </div>
  )
}
